export type ConsentCategory = 'analytics' | 'marketing';

export interface ConsentPreferences {
  analytics: boolean;
  marketing: boolean;
}

export const CONSENT_COOKIE = 'cookie_consent';

export const CONSENT_EXPIRY_DAYS = 365;
export const CONSENT_VERSION = 1;

function getCookie(name: string): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const match = document.cookie.match(
    new RegExp(
      '(?:^|; )' + name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '=([^;]*)'
    )
  );
  return match ? decodeURIComponent(match[1]) : undefined;
}

function setCookie(name: string, value: string, days: number) {
  if (typeof document === 'undefined') return;
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(
    value
  )}; expires=${expires}; path=/; SameSite=Lax`;
}

export function getConsent(): ConsentPreferences | null {
  const raw = getCookie(CONSENT_COOKIE);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      typeof parsed.analytics === 'boolean' &&
      typeof parsed.marketing === 'boolean' &&
      parsed.v === CONSENT_VERSION
    ) {
      return { analytics: parsed.analytics, marketing: parsed.marketing };
    }
    return null;
  } catch {
    return null;
  }
}

export function setConsent(preferences: ConsentPreferences) {
  setCookie(
    CONSENT_COOKIE,
    JSON.stringify({ ...preferences, v: CONSENT_VERSION }),
    CONSENT_EXPIRY_DAYS
  );
}

export function isGpcEnabled(): boolean {
  if (typeof navigator === 'undefined') return false;
  return (
    'globalPrivacyControl' in navigator &&
    navigator.globalPrivacyControl === true
  );
}

declare global {
  interface Navigator {
    globalPrivacyControl?: boolean;
  }
}

export function hasConsent(category: ConsentCategory): boolean {
  if (isGpcEnabled()) return false;

  const consent = getConsent();
  if (!consent) return false;
  return consent[category] === true;
}
